import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/authContext';
import { projectApiConnect } from '../lib/api/projectApi.connect';
import { uploadAttachmentViaTesoro } from '../lib/uploadAttachment';
/**
 * Fetch list of files for a project with optional filtering and pagination
 */ export function useProjectFiles(projectId, params) {
    return useQuery({
        queryKey: [
            'projectFiles',
            projectId,
            params?.search,
            params?.sortBy,
            params?.sortOrder,
            params?.page,
            params?.limit
        ],
        queryFn: ()=>{
            if (!projectId) {
                throw new Error('Project ID is required');
            }
            return projectApiConnect.listProjectFiles(projectId, params);
        },
        // No flag gate any more: project data is Connect-only, so this no longer
        // has to wait for Flipt to answer before it can pick a transport.
        enabled: !!projectId,
        staleTime: 30000
    });
}
/**
 * Upload files to a project.
 *
 * Each file is pushed via tesoro: presigned PUT direct to MinIO, no bytes
 * through sabaody or zou. The returned artifact ids are then registered
 * on the project via `projectDataClient.uploadProjectFiles({ projectId,
 * artifactIds })` which creates an Attachment row keyed by artifact id.
 *
 * autoProcess is false here — vendor library and proposal flows trigger
 * processing through ProcessBatch RPC after upload, not via tesoro's
 * single-doc auto-process kick-off.
 */ export function useUploadProjectFiles() {
    const queryClient = useQueryClient();
    const { user } = useAuth();
    return useMutation({
        mutationFn: async ({ projectId, files })=>{
            if (!user || !user.tenant_id) {
                throw new Error('Cannot upload: no authenticated user');
            }
            const tenantId = user.tenant_id;
            const ownerUserId = user.id;
            const settled = await Promise.allSettled(files.map((file)=>uploadAttachmentViaTesoro(file, {
                    tenantId,
                    ownerUserId,
                    projectId,
                    autoProcess: false
                })));
            const artifactIds = [];
            const failed = [];
            settled.forEach((r, i)=>{
                if (r.status === 'fulfilled') {
                    artifactIds.push(r.value.artifactId);
                } else {
                    failed.push({
                        name: files[i].name,
                        error: r.reason instanceof Error ? r.reason.message : String(r.reason)
                    });
                }
            });
            if (artifactIds.length === 0) {
                return {
                    files: [],
                    failed
                };
            }
            const linked = await projectApiConnect.attachArtifactsToProject(projectId, artifactIds);
            return {
                files: linked.files,
                failed: [
                    ...failed,
                    ...linked.failed
                ]
            };
        },
        onSuccess: (_data, { projectId })=>{
            queryClient.invalidateQueries({
                queryKey: [
                    'projectFiles',
                    projectId
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    'project',
                    projectId
                ]
            });
        }
    });
}
/**
 * Delete a file from a project
 */ export function useDeleteProjectFile() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ projectId, attachmentId })=>projectApiConnect.deleteProjectFile(projectId, attachmentId),
        onSuccess: (_data, { projectId })=>{
            // Invalidate project files list to refetch
            queryClient.invalidateQueries({
                queryKey: [
                    'projectFiles',
                    projectId
                ]
            });
            // Also invalidate project to update lastActivityAt
            queryClient.invalidateQueries({
                queryKey: [
                    'project',
                    projectId
                ]
            });
        }
    });
}
